/** @format */

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	getAccountRequirement,
	updateStripeAccountDetails,
} from "../../../redux/actions/circleAction";
import {
	Box,
	Alert,
	TextField,
	Button,
	Stack,
	Typography,
	Paper,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { colors } from "../../../utility/types/constant";
import { selectAccountRequirements } from "../../../redux/selectors/accountVerification";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";

interface CountryConfig {
	code: string;
	name: string;
	requiresSSN: boolean;
	nationalIdLabel: string;
	currency: string;
}

interface EnterStripeDetailsFormProps {
	userId: string;
	countryConfig?: CountryConfig | null;
}

const EnterStripeDetailsForm = ({
	userId,
	countryConfig: propCountryConfig,
}: EnterStripeDetailsFormProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const { countryConfig: stateCountryConfig } = useSelector(
		selectAccountRequirements,
	);

	// Use prop if provided, otherwise use state
	const countryConfig = propCountryConfig ?? stateCountryConfig;

	const isUSAccount =
		countryConfig?.code === "US" || countryConfig?.requiresSSN === true;

	const [ssn, setSsn] = useState("");
	const [dob, setDob] = useState({
		day: "",
		month: "",
		year: "",
	});
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const handleSubmit = async () => {
		if (!countryConfig) {
			setError(
				"Account country information is still loading. Please try again.",
			);
			return;
		}
		// Only validate SSN for US accounts
		if (isUSAccount && ssn.length !== 4) {
			setError("Please enter exactly 4 digits");
			return;
		}

		if (!dob.day || !dob.month || !dob.year) {
			setError("Please enter your full date of birth");
			return;
		}

		setError(null);
		setLoading(true);

		try {
			
			const payload: any = {
				userId,
				dob: {
					day: Number(dob.day),
					month: Number(dob.month),
					year: Number(dob.year),
				},
			};

			console.log("payload before dispatch:", payload);

			if (isUSAccount) {
				payload.ssn_last_4 = ssn.trim();
			}

			await dispatch(updateStripeAccountDetails(payload)).unwrap();
			await dispatch(getAccountRequirement(userId)).unwrap();
		} catch (error: any) {
			setError("Something unexpected happened. Please try again");
		} finally {
			setLoading(false);
		}
	};

	// Determine if submit should be disabled
	const isSubmitDisabled = () => {
		if (!countryConfig) return true;
		if (!dob.day || !dob.month || !dob.year) return true;
		if (loading) return true;
		if (isUSAccount && ssn.length !== 4) return true;
		return false;
	};

	return (
		<Paper
			elevation={0}
			sx={{
				mt: 2,
				mb: 4,
				width: { xs: "100%", sm: 420 },
				maxWidth: "100%",
				borderRadius: 3,
				border: `1px solid ${colors.mainSecondary}20`,
				overflow: "hidden",
			}}>
			{/* Header Section */}
			<Box
				sx={{
					background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary}dd 100%)`,
					p: 3,
					color: colors.white,
				}}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
					<VerifiedUserOutlinedIcon sx={{ fontSize: 28 }} />
					<Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
						Verify Your Identity
					</Typography>
				</Box>
				<Typography
					variant="body2"
					sx={{ opacity: 0.9, lineHeight: 1.6, fontSize: "0.9rem" }}>
					To comply with global financial regulations and protect our community,
					we need to verify a few additional details.
				</Typography>
			</Box>

			{/* Form Section */}
			<Box sx={{ p: 3, backgroundColor: colors.white }}>
				<Stack spacing={2.5}>
					{error && (
						<Alert
							severity="error"
							sx={{
								borderRadius: 2,
								"& .MuiAlert-message": { width: "100%" },
							}}>
							{error}
						</Alert>
					)}

					{/* Country Badge */}
					{countryConfig && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 1,
								py: 1,
								px: 2,
								backgroundColor: `${colors.mainSecondary}10`,
								borderRadius: 2,
								border: `1px solid ${colors.mainSecondary}30`,
							}}>
							<Typography
								variant="body2"
								sx={{
									color: colors.primary,
									fontWeight: 500,
									fontSize: "0.85rem",
								}}>
								Account Region:{" "}
								<span style={{ fontWeight: 600 }}>{countryConfig.name}</span>
							</Typography>
						</Box>
					)}

					{/* SSN Field - US Only */}
					{isUSAccount && (
						<Box>
							<TextField
								label="Last 4 digits of SSN"
								value={ssn}
								onChange={(e) => setSsn(e.target.value.replace(/\D/g, ""))}
								inputProps={{ maxLength: 4 }}
								fullWidth
								placeholder="XXXX"
								helperText="Required for US-based accounts only"
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: 2,
										backgroundColor: "#fafafa",
										"& fieldset": {
											borderColor: `${colors.primary}30`,
										},
										"&:hover fieldset": {
											borderColor: colors.mainSecondary,
										},
										"&.Mui-focused fieldset": {
											borderColor: colors.mainSecondary,
											borderWidth: 2,
										},
									},
									"& .MuiInputLabel-root": {
										color: colors.primary,
										"&.Mui-focused": {
											color: colors.mainSecondary,
										},
									},
									"& .MuiFormHelperText-root": {
										fontSize: "0.75rem",
										color: "#666",
									},
								}}
							/>
						</Box>
					)}

					{/* Date of Birth Field */}
					<Box>
						<TextField
							label="Date of Birth"
							type="date"
							value={
								dob.year && dob.month && dob.day ?
									`${dob.year}-${dob.month.padStart(2, "0")}-${dob.day.padStart(
										2,
										"0",
									)}`
								:	""
							}
							onChange={(e) => {
								const [year, month, day] = e.target.value.split("-");
								setDob({ year, month, day });
							}}
							InputLabelProps={{
								shrink: true,
							}}
							fullWidth
							helperText="Used to verify your identity"
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: 2,
									backgroundColor: "#fafafa",
									"& fieldset": {
										borderColor: `${colors.primary}30`,
									},
									"&:hover fieldset": {
										borderColor: colors.mainSecondary,
									},
									"&.Mui-focused fieldset": {
										borderColor: colors.mainSecondary,
										borderWidth: 2,
									},
								},
								"& .MuiInputLabel-root": {
									color: colors.primary,
									"&.Mui-focused": {
										color: colors.mainSecondary,
									},
								},
								"& .MuiFormHelperText-root": {
									fontSize: "0.75rem",
									color: "#666",
								},
							}}
						/>
					</Box>

					{/* Submit Button */}
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={isSubmitDisabled()}
						sx={{
							mt: 1,
							py: 1.5,
							color: colors.white,
							backgroundColor: colors.mainSecondary,
							borderRadius: 2,
							fontWeight: 600,
							fontSize: "1rem",
							textTransform: "none",
							boxShadow: `0 4px 14px ${colors.mainSecondary}40`,
							"&:hover": {
								backgroundColor: colors.mainSecondary,
								boxShadow: `0 6px 20px ${colors.mainSecondary}50`,
								transform: "translateY(-1px)",
							},
							"&:disabled": {
								backgroundColor: "#ccc",
								boxShadow: "none",
							},
							transition: "all 0.2s ease-in-out",
						}}
						fullWidth>
						{loading ?
							<LoadingSpinner loading={loading} size={20} />
						:	"Continue Verification"}
					</Button>

					{/* Security Note */}
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							gap: 1,
							pt: 1.5,
							borderTop: `1px solid ${colors.primary}10`,
						}}>
						<LockOutlinedIcon
							sx={{
								fontSize: 16,
								color: colors.primary,
								mt: 0.3,
								opacity: 0.7,
							}}
						/>
						<Typography
							variant="caption"
							sx={{
								color: "#666",
								lineHeight: 1.5,
								fontSize: "0.75rem",
							}}>
							Your information is encrypted and securely transmitted to our
							payment partner, Stripe. We never store sensitive data on our
							servers.
						</Typography>
					</Box>
				</Stack>
			</Box>
		</Paper>
	);
};

export default EnterStripeDetailsForm;
